import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../../environments/environment';

interface WebSocketMessage {
  topic?: string;
  message_type?: string;
  payload?: string;
  data?: unknown;
}

interface OutgoingMessage {
  action: 'subscribe' | 'unsubscribe' | 'ping';
  topic?: string;
  token?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class WebsocketService {
  private readonly ngZone = inject(NgZone);

  private socket: WebSocket | null = null;
  private readonly WS_URL = environment.MQTT_WS_URL;
  private readonly reconnectDelay = 5000;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  /** True between connect() and disconnect(); guards the auto-reconnect loop so a
   *  logout-triggered close doesn't immediately reopen the socket. */
  private connectionRequested = false;
  private readonly messageQueue: OutgoingMessage[] = [];

  /** Tracked topics for auto-resubscribe after a transient disconnect. A Set avoids duplicates. */
  private readonly topicsWithToken = new Set<string>();

  /** Flipped true after the first successful onopen. Lets us distinguish the initial
   *  connect (where subscribeToTopic owns the first subscribe via the queue) from a
   *  reconnect (where the auto-resubscribe loop needs to replay every known topic). */
  private hasOpenedBefore = false;

  private readonly messageSubject = new Subject<unknown>();
  readonly messages$: Observable<unknown> = this.messageSubject.asObservable();

  /** In-memory token pushed by AuthService. The BE sets the access-token cookie as
   *  httpOnly so JS can't read it directly — AuthService receives the token in the
   *  login/refresh response body and hands it to us via setAccessToken(). */
  private currentToken: string | null = null;

  // No eager constructor connect — subscribe-on-login drives the lifecycle so
  // anonymous tabs never open a socket. Logout calls disconnect() to tear down.

  private connect(): void {
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) return;

    this.connectionRequested = true;
    this.socket = new WebSocket(this.WS_URL);

    this.socket.onopen = () => {
      console.log('[WebSocket] Connected');

      // Re-send queued messages first so the BE sees them in submission order.
      while (this.messageQueue.length > 0) {
        const next = this.messageQueue.shift();
        if (next) this.sendMessage(next);
      }

      // Auto-resubscribe only on reconnects. The initial open already has the
      // subscribe in the queue (just flushed above), so doing it here too would
      // send a duplicate frame.
      if (this.hasOpenedBefore) {
        this.topicsWithToken.forEach((topic) => {
          const token = this.readAccessToken();
          if (token) this.sendMessage({ action: 'subscribe', topic, token });
        });
      }
      this.hasOpenedBefore = true;
    };

    this.socket.onmessage = (event: MessageEvent<string>) => {
      this.ngZone.run(() => this.handleMessage(event));
    };

    this.socket.onerror = (err) => {
      console.error('[WebSocket] Error:', err);
    };

    this.socket.onclose = () => {
      console.warn('[WebSocket] Disconnected.');
      this.socket = null;
      if (!this.connectionRequested) return;
      if (this.reconnectTimeout !== null) clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = setTimeout(() => this.connect(), this.reconnectDelay);
    };
  }

  // --------- Subscriptions ---------

  subscribeToUserAlert(userId: number | string): void {
    // Reopen the socket if a previous disconnect() closed it (e.g. logout → login again).
    if (!this.socket) this.connect();
    const topic = `admin.user.${userId}.alerts`;
    void this.subscribeToTopic(topic, { userId });
  }

  /** Subscribe to the shared realtime channels for a Post Mortem Report's collaborative
   *  editor: document (`.sync`) and awareness/presence (`.presence`). Fan-out payloads
   *  arrive on `messages$`; they are self-describing (`event`/`showId`) so consumers
   *  filter without needing the MQTT topic. */
  subscribeToPostMortemReport(showId: number | string): void {
    if (!this.socket) this.connect();
    void this.subscribeToTopic(`admin.post-mortem-report.${showId}.sync`, {});
    void this.subscribeToTopic(`admin.post-mortem-report.${showId}.presence`, {});
  }

  /** Tear down both Post Mortem channels (call on editor destroy). */
  unsubscribeFromPostMortemReport(showId: number | string): void {
    this.unsubscribeFromTopic(`admin.post-mortem-report.${showId}.sync`);
    this.unsubscribeFromTopic(`admin.post-mortem-report.${showId}.presence`);
  }

  // --------- Core subscription helpers ---------

  private async subscribeToTopic(topic: string, data: Record<string, unknown>): Promise<void> {
    if (this.topicsWithToken.has(topic)) {
      console.log(`[WebSocket] Already subscribed to topic: ${topic}`);
      return;
    }
    // Reserve the topic BEFORE the async wait so two synchronous callers (e.g.
    // AuthService.connectRealtime + App.ngOnInit) don't both pass the has() check
    // and end up sending two subscribe frames.
    this.topicsWithToken.add(topic);

    try {
      const token = await this.waitForToken();
      this.sendMessage({ action: 'subscribe', topic, ...data, token });
      console.log('[WebSocket] Subscribed to:', topic);
    } catch (error) {
      // Roll back so a retry can succeed.
      this.topicsWithToken.delete(topic);
      console.error('Token was not available for subscription:', error);
    }
  }

  unsubscribeFromTopic(topic: string): void {
    this.sendMessage({ action: 'unsubscribe', topic });
    this.topicsWithToken.delete(topic);
    console.log('[WebSocket] Unsubscribed from:', topic);
  }

  unsubscribeRemainingTopics(): void {
    this.topicsWithToken.forEach((topic) => this.unsubscribeFromTopic(topic));
    this.topicsWithToken.clear();
  }

  /** Logout-time teardown: unsubscribe all topics, close the socket, and stop
   *  any pending reconnect so it doesn't reopen behind the user's back. */
  disconnect(): void {
    this.connectionRequested = false;
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.unsubscribeRemainingTopics();
    this.socket?.close();
    this.socket = null;
    this.messageQueue.length = 0;
    // Reset so the next connect() (re-login same tab) treats its first open as
    // a fresh initial connect and doesn't fire the reconnect auto-resubscribe.
    this.hasOpenedBefore = false;
  }

  // --------- Utilities ---------

  /** Polls the access-token cookie (set by the login API response) every 200ms for up
   *  to 5s. Lets `subscribeToUserAlert` be called immediately after `setAccessToken`
   *  without a race against the cookie write. */
  private async waitForToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const token = this.readAccessToken();
        if (token) {
          clearInterval(interval);
          resolve(token);
        }
      }, 200);

      setTimeout(() => {
        clearInterval(interval);
        reject(new Error('Token not available'));
      }, 5000);
    });
  }

  /** AuthService pushes the freshly-issued access token here after login or silent
   *  refresh, so subscribes can carry it without needing to read the httpOnly cookie.
   *  When the token changes while topics are already tracked, re-send subscribe frames
   *  so the MQTT bridge does not keep rejecting with INVALID_TOKEN after a refresh. */
  setAccessToken(token: string | null): void {
    this.currentToken = token;
    if (token && this.topicsWithToken.size > 0) {
      this.resubscribeTrackedTopics();
    }
  }

  /** Re-assert every tracked topic with the current access token (after refresh/reconnect). */
  private resubscribeTrackedTopics(): void {
    const token = this.readAccessToken();
    if (!token) return;
    this.topicsWithToken.forEach((topic) => {
      this.sendMessage({ action: 'subscribe', topic, token });
    });
  }

  private readAccessToken(): string | null {
    return this.currentToken;
  }

  sendPing(): void {
    this.sendMessage({ action: 'ping' });
  }

  sendMessage(message: OutgoingMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.messageQueue.push(message);
    }
  }

  private handleMessage(event: MessageEvent<string>): void {
    if (!event.data) {
      console.warn('Received empty event data');
      return;
    }
    let message: WebSocketMessage;
    try {
      message = JSON.parse(event.data) as WebSocketMessage;
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      return;
    }
    if (message.topic === '__error__' && message.message_type === 'error') {
      this.handleServerError(message.payload);
      return;
    }
    if (typeof message.payload === 'string') {
      try {
        const payload: unknown = JSON.parse(message.payload);
        this.messageSubject.next(payload);
      } catch (error) {
        console.error('Error parsing WebSocket payload:', error);
      }
      return;
    }
    if (message.data !== undefined) {
      this.messageSubject.next(message.data);
    }
  }

  /** Server-side error envelope. Drops the rejected topic from our tracked Set so the
   *  next reconnect doesn't auto-resubscribe and re-trigger the same rejection in a
   *  tight loop. Does NOT emit to `messages$` — consumers shouldn't see protocol-level
   *  errors as alert payloads. */
  private handleServerError(payloadString: string | undefined): void {
    if (typeof payloadString !== 'string') return;
    let parsed: { code?: unknown; message?: unknown };
    try {
      parsed = JSON.parse(payloadString) as { code?: unknown; message?: unknown };
    } catch (error) {
      console.error('[WebSocket] Failed to parse server error payload:', error);
      return;
    }
    console.error('[WebSocket] Server error:', parsed);
    // Server messages of the form `Subscribe rejected for "<topic>"` quote the topic
    // — pull it out and stop tracking it so we don't keep retrying.
    if (typeof parsed.message === 'string') {
      const match = /"([^"]+)"/.exec(parsed.message);
      if (match) this.topicsWithToken.delete(match[1] as string);
    }
  }
}
